import React, { useMemo, useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Button } from "../components/ui/button";
import axios from "axios";

function ImportHistoryTable({ imports, groups, formatDateISOToGerman, onRefresh, apiBase, authHeaders }) {
  const [page, setPage] = useState(0); // 0-basierter Seitenindex
  const pageSize = 10;

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(imports.length / pageSize));
  }, [imports.length]);

  const currentPageImports = useMemo(() => {
    const start = page * pageSize;
    const end = start + pageSize;
    return imports.slice(start, end);
  }, [imports, page]);

  function handlePrevPage() {
    setPage((p) => Math.max(0, p - 1));
  }

  function handleNextPage() {
    setPage((p) => Math.min(totalPages - 1, p + 1));
  }

  async function handleDeleteImport(id, name) {
    if (!window.confirm(`Möchten Sie den Importbericht "${name}" wirklich löschen?`)) {
      return;
    }
    try {
      await axios.delete(`${apiBase}/api/import/children/${id}`, authHeaders());
      if (onRefresh) onRefresh();
    } catch (e) {
      console.error(e);
      alert("Der Importbericht konnte nicht gelöscht werden.");
    }
  }

  return (
    <div className="space-y-2" data-testid="import-history-wrapper">
      <div className="max-h-64 overflow-y-auto rounded-md border border-slate-200 bg-slate-50/80 p-2">
        <Table data-testid="import-history-table">
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs">Name</TableHead>
              <TableHead className="text-xs whitespace-nowrap">Datum</TableHead>
              <TableHead className="text-xs">Gruppe</TableHead>
              <TableHead className="text-xs text-right whitespace-nowrap">Typ</TableHead>
              <TableHead className="text-xs text-right whitespace-nowrap">Anzahl Kinder</TableHead>
              <TableHead className="text-xs text-right whitespace-nowrap">Aktion</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {currentPageImports.map((imp) => {
              const groupName =
                imp.group_id && groups.find((g) => g.id === imp.group_id)
                  ? groups.find((g) => g.id === imp.group_id).name
                  : "-";
              const dateStr = imp.created_at
                ? formatDateISOToGerman(
                    typeof imp.created_at === "string" ? imp.created_at.slice(0, 10) : imp.created_at,
                  )
                : "";
              return (
                <TableRow key={imp.id} data-testid={`import-row-${imp.id}`}>
                  <TableCell className="text-xs text-slate-800">{imp.name}</TableCell>
                  <TableCell className="text-xs text-slate-700 whitespace-nowrap">{dateStr}</TableCell>
                  <TableCell className="text-xs text-slate-700">{groupName}</TableCell>
                  <TableCell className="text-xs text-right uppercase text-slate-700">
                    {imp.type === "neu" ? "NEU" : "BESTAND"}
                  </TableCell>
                  <TableCell className="text-xs text-right text-slate-700">{imp.row_count}</TableCell>
                  <TableCell className="text-xs text-right text-slate-700">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-700 hover:text-red-900 hover:bg-red-50"
                      onClick={() => handleDeleteImport(imp.id, imp.name)}
                      data-testid={`import-delete-button-${imp.id}`}
                    >
                      Bericht löschen
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-between text-xs text-slate-600 mt-1">
        <span>
          Seite {page + 1} von {totalPages}, Einträge {page * pageSize + 1}–
          {Math.min((page + 1) * pageSize, imports.length)} von {imports.length}
        </span>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handlePrevPage}
            disabled={page === 0}
            data-testid="import-page-prev"
          >
            « Zurück
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleNextPage}
            disabled={page >= totalPages - 1}
            data-testid="import-page-next"
          >
            Weiter »
          </Button>
        </div>
      </div>
    </div>
  );
}

export default ImportHistoryTable;
